import type _jquery from "jquery";
import { exhaustiveGuard, isStringLiteralOfType, type Nil } from "@mcwd/typescript-type-guards";
import { GatingState, type GatingStateFor } from "../../types/definitions/gating/gating-state.js";
import { toValue, type MaybeRefOrGetter } from "vue";
import { GatingLevel, type GatingLevelFor } from "../../types/definitions/gating/gating-level.js";
import type { FormOrFormExperienceType } from "./FormOrFormExperienceType.js";

function getGatingStateFromUserGatingCookie<
  TFormOrExperience extends FormOrFormExperienceType,
  TGatingState extends GatingState.FormExperience
>({
  cookieStateValue,
  initialGatingLevel,
  allowedGatingStates: _allowedGatingStates
}: {
  cookieStateValue: GatingState.UserCookie,
  initialGatingLevel: GatingLevelFor<TFormOrExperience>,
  allowedGatingStates: readonly TGatingState[]
}): GatingStateFor<TFormOrExperience> 
{
  type ThisReturnType = ReturnType<typeof getGatingStateFromUserGatingCookie<TFormOrExperience, TGatingState>>;
  if (initialGatingLevel === "NoGate") {
    return "NoGate" as ThisReturnType;
  }  
  if (isStringLiteralOfType(initialGatingLevel, GatingLevel.UserCookie)) {
    switch (cookieStateValue) {
      case "NeedsForm":
        return initialGatingLevel as ThisReturnType;
      case "PartialGate":
        return (initialGatingLevel === "PartialGate" ? "BlindSubmit" : initialGatingLevel) as ThisReturnType;
      case "FullGate":
        return "BlindSubmit" as ThisReturnType;
      default: exhaustiveGuard(cookieStateValue);
    }
  }
  exhaustiveGuard(initialGatingLevel);
}

export function getGatingStateFromDynamicValues<TFormOrExperience extends FormOrFormExperienceType, TGatingState extends GatingState.FormExperience>({
  type: _type,
  initialGatingLevel,
  isMarketoKnownVisitor,
  allowedGatingStates,
  cookieGatingLevelState
}: {
  type: TFormOrExperience,
  isMarketoKnownVisitor: MaybeRefOrGetter<boolean | Nil>,
  allowedGatingStates: TGatingState[] | readonly TGatingState[],
  cookieGatingLevelState: MaybeRefOrGetter<GatingState.UserCookie>,
  initialGatingLevel: GatingLevelFor<TFormOrExperience>
}): GatingStateFor<TFormOrExperience>
{
  const hasMarketoCookie = toValue(isMarketoKnownVisitor) === true;
  if (hasMarketoCookie && allowedGatingStates.includes("BlindSubmit" as TGatingState)) {
    const cookieStateValue = toValue(cookieGatingLevelState);
    return getGatingStateFromUserGatingCookie({ cookieStateValue, initialGatingLevel, allowedGatingStates });
  }
  return initialGatingLevel;
}